import { Box, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { MemoEmptyChart } from 'charts/common/EmptyChart';
import type { BuildChartContext, ChartData } from '../catalog/types';
import { CHART_AXIS_MARGIN } from '../tokens/muiChartLayout';
import type { ReactElement } from 'react';
import { truncateAxisLabel } from '../formatters/truncateAxisLabel';
import { getSliceGradientFill } from '../theme/chartGradients';
import { ChartSvgDefs } from './ChartSvgDefs';
import {
  CHART_AXIS_LABEL_MAX_LENGTH,
  getChartAreaSx,
  getChartAxisSlotProps,
  getChartGridColor,
} from '../theme/muiChartSlotProps';

type BarChartViewProps = Readonly<{
  chartId: string;
  data: ChartData;
  height: number;
  colors: readonly string[];
  variant: BuildChartContext['variant'];
}>;

export function BarChartView({
  chartId,
  data,
  height,
  colors,
  variant,
}: BarChartViewProps): ReactElement {
  const theme = useTheme();

  if (data.values.length === 0) {
    return <MemoEmptyChart variant={variant} />;
  }

  const maxLabelLength = CHART_AXIS_LABEL_MAX_LENGTH[variant];
  const axisLabels = data.labels.map((label) =>
    variant === 'dashboard' ? truncateAxisLabel(label, maxLabelLength) : label
  );

  const usedColors = data.values.map(
    (_, index) => colors[index % colors.length] ?? colors[0]
  );

  const chart = (
    <>
      <ChartSvgDefs chartId={chartId} colors={usedColors} />
      <BarChart
        xAxis={[
          {
            scaleType: 'band',
            data: axisLabels,
            tickLabelStyle: getChartAxisSlotProps(variant).tickLabelStyle,
          },
        ]}
        yAxis={[
          {
            tickLabelStyle: getChartAxisSlotProps(variant).tickLabelStyle,
          },
        ]}
        grid={{ horizontal: true }}
        sx={{
          '& .MuiChartsGrid-line': {
            stroke: getChartGridColor(theme),
            strokeOpacity: 0.4,
          },
        }}
        hideLegend
        series={[
          {
            data: [...data.values],
            colorGetter: ({ dataIndex }) =>
              getSliceGradientFill(chartId, dataIndex),
            ...(variant === 'details' ? { barLabel: 'value' as const } : {}),
          },
        ]}
        height={height}
        margin={CHART_AXIS_MARGIN}
      />
    </>
  );

  if (variant === 'dashboard') {
    return (
      <Box
        sx={{
          width: '100%',
          flex: '0 0 auto',
          minWidth: 0,
          height,
          overflow: 'hidden',
        }}
      >
        {chart}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...getChartAreaSx(),
        height,
        minHeight: height,
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
        }}
      >
        {chart}
      </Box>
    </Box>
  );
}
